import type { InquiryItem } from '@/components/molecules/InquiriesTable'
import type { OrgMemberInquiries } from '@/types/organization'

export interface MemberInquiriesBaseProps {
  /** Name (or email) of the member whose activity this is. */
  memberLabel: string
  items: InquiryItem[]
  isLoading: boolean
}

/**
 * Merge the two independently paginated blocks into one newest-first list.
 *
 * `GET /organizations/{orgId}/members/{userId}/inquiries` returns bids and RFQs
 * separately, in the exact `{page, items, total, data}` shape that `/bids` and
 * `/rfq` use — they come from the same backend helpers — so this is the same
 * merge `InquiriesTable` already performs for the current user.
 */
export const mergeMemberInquiries = (data: OrgMemberInquiries | null): InquiryItem[] => {
  if (!data) return []

  const merged: InquiryItem[] = [
    ...(data.bids?.data ?? []).map((bid) => ({ ...bid, inquiryType: 'bid' as const })),
    ...(data.rfqs?.data ?? []).map((rfq) => ({ ...rfq, inquiryType: 'rfq' as const })),
  ]

  return merged.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
}

/**
 * Page count for the merged view.
 *
 * The two blocks paginate independently against one shared `limit`/`offset`, so
 * the merged list runs out only when BOTH blocks do — hence `max`, not a total of
 * the two. This is why the panel labels the counts per block rather than pretending
 * there is a single combined sequence.
 */
export const mergedPageCount = (data: OrgMemberInquiries | null, limit: number): number => {
  if (!data || limit <= 0) return 0
  const largestBlock = Math.max(data.bids?.total ?? 0, data.rfqs?.total ?? 0)
  return Math.ceil(largestBlock / limit)
}
